import React, { cloneElement } from 'react';
import { Card as MuiCard, Table as MuiTable } from '@mui/material';
import { withStyles } from '@mui/styles';

import { ActionBar as DefaultActionBar } from './action-bar.table';
import { TableBody as DefaultTableBody } from './body.table';
import { TableFooter as DefaultTableFooter } from './footer.table';
import { TableHeader as DefaultTableHeader } from './header.table';
import { TableSkeleton } from './skeleton.table';
import { TableContext } from './context.table';

const styles = () => ({
  container: {
    position: 'relative',
    overflowX: 'auto',
  },
  containerBlank: {
    position: 'relative',
    background: 'inherit',
  },
});

export const BaseTable = withStyles(styles)(
  ({
    cardProps,
    classes,
    children,
    context,
    fixed = false,
    loading = true,
    rowHover,
    ...props
  }) => {
    const {
      ActionBar,
      TableHeader,
      TableBody,
      TableFooter,
      QuickView,
      ...childOverrides
    } = createChildHash(children);

    if (loading) {
      return (
        <TableSkeleton
          children={children}
          classes={classes}
          columns={props.columns}
          context={context}
          data={props.data}
          loading={loading}
          fixed={fixed}
          rowHover={rowHover}
          {...props}
        />
      );
    }

    const {
      data,
      history,
      location,
      match,
      columns,
      q,
      rqps,
      rowsperpage,
      rowsperpageoptions,
      count,
      ...htmlAttributes
    } = props;

    return (
      <TableContext.Provider
        value={{
          id: props.id,
          columns: props.columns,
          data: props.data,
          rowHover,
          childOverrides,
          loading,
          fixed,
          ...context,
        }}
      >
        <>
          {ActionBar ? (
            cloneElement(ActionBar, { ...props, ctx: context })
          ) : (
            <DefaultActionBar />
          )}
          <MuiCard
            className={
              ActionBar && ActionBar.props.classes
                ? classes.containerBlank
                : `custom-scrollbar ${classes.container}`
            }
            {...cardProps}
          >
            <MuiTable
              {...htmlAttributes}
              style={{ tableLayout: fixed && 'fixed' }}
              aria-label="sticky table"
            >
              {TableHeader ? (
                cloneElement(TableHeader, props)
              ) : (
                <DefaultTableHeader />
              )}
              {TableBody ? (
                cloneElement(TableBody, { childOverrides, loading, ...props })
              ) : (
                <DefaultTableBody loading={loading} />
              )}
            </MuiTable>
            {TableFooter ? (
              cloneElement(TableFooter, { childOverrides, ...props })
            ) : (
              <DefaultTableFooter />
            )}
          </MuiCard>
          {QuickView && cloneElement(QuickView, props)}
        </>
      </TableContext.Provider>
    );
  }
);

function createChildHash(children) {
  return React.Children.toArray(children).reduce((acc, child) => {
    return {
      ...acc,
      [child.type.displayName || child.type.name]: child,
    };
  }, {});
}

BaseTable.defaultProps = {
  columns: [],
  data: [],
  loading: false,
  rowHover: false,
};
